import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Save, AlertCircle } from 'lucide-react';

const EditConsumptionDataModal = ({ report, onClose, onUpdate, getAuthHeader }) => {
  const [segment, setSegment] = useState(report.segment || '');
  const [powers, setPowers] = useState({
    base: report.subscribed_powers?.base || '',
    hc: report.subscribed_powers?.hc || '',
    hp: report.subscribed_powers?.hp || '',
    hcb: report.subscribed_powers?.hcb || '',
    hch: report.subscribed_powers?.hch || '',
    hph: report.subscribed_powers?.hph || '',
    hpb: report.subscribed_powers?.hpb || '',
    hce: report.subscribed_powers?.hce || '',
    hpe: report.subscribed_powers?.hpe || '',
    pointe: report.subscribed_powers?.pointe || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const powerLabels = {
    base: 'Base',
    hc: 'Heures Creuses (HC)',
    hp: 'Heures Pleines (HP)',
    hcb: 'Heures Creuses Bleues (HCB)',
    hch: 'Heures Creuses Hiver (HCH)',
    hph: 'Heures Pleines Hiver (HPH)',
    hpb: 'Heures Pleines Bleues (HPB)',
    hce: 'Heures Creuses Été (HCE)',
    hpe: 'Heures Pleines Été (HPE)',
    pointe: 'Pointe',
  };

  const handlePowerChange = (type, value) => {
    // Allow empty string or valid numbers
    if (value === '' || !isNaN(value)) {
      setPowers(prev => ({
        ...prev,
        [type]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert empty strings to null and numbers to floats
      const subscribedPowers = {};
      Object.keys(powers).forEach(key => {
        subscribedPowers[key] = powers[key] === '' ? null : parseFloat(powers[key]);
      });

      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/consumption-reports/${report.share_token}`,
        {
          segment: segment || null,
          subscribed_powers: subscribedPowers
        },
        {
          headers: getAuthHeader()
        }
      );

      if (response.data.success) {
        onUpdate(response.data.report);
        onClose();
      }
    } catch (err) {
      console.error('Error updating report:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Modifier les données techniques</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Segment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Segment tarifaire
            </label>
            <input
              type="text"
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              placeholder="Ex: C3, C4, C5..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Segment tarifaire du contrat (C2, C3, C4, C5, etc.)
            </p>
          </div>

          {/* Subscribed Powers */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Puissances souscrites (kW)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(powerLabels).map(([type, label]) => (
                <div key={type}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {label}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={powers[type]}
                    onChange={(e) => handlePowerChange(type, e.target.value)}
                    placeholder="--"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  />
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Laissez vide si la puissance n'est pas applicable. Les valeurs seront affichées uniquement si elles sont renseignées.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditConsumptionDataModal;
